import { useCallback, useEffect, useState, type ReactElement } from "react";

import { api, ApiError } from "../../api/client";
import type { AdminDeletionRequest, AdminParticipant, AdminSubstudy, DeletionExecuteResult } from "../../api/types";
import { ConfirmDialog } from "../ConfirmDialog";
import { ErrorNotice } from "../ErrorNotice";
import { SkeletonRows } from "../Skeleton";
import { TabPanel } from "../Tabs";

// Exit-survey reasons given with a deletion request (null on pre-0019 rows).
const REASON_LABELS: Record<string, string> = {
  privacy_concerns: "Privacy concerns",
  too_time_consuming: "Too time-consuming",
  technical_issues: "Technical issues",
  no_longer_interested: "No longer interested",
  believe_inaccurate: "Believe results are inaccurate",
  other: "Other",
};

// Participant corrections (ARCHITECTURE.md substudy-linkage piece 2) + the
// deletion-request queue — the ONLY place a participant's data is ever deleted.
export function ParticipantsTab({
  substudies,
  onDeletionsResolved,
}: {
  substudies: AdminSubstudy[];
  // Lets the shell re-count the queue for its tab badge once a request stops
  // being pending. Optional so the tab still stands alone (e.g. in tests).
  onDeletionsResolved?: () => void;
}): ReactElement {
  const [searchEmail, setSearchEmail] = useState("");
  const [found, setFound] = useState<AdminParticipant[] | null>(null);
  const [reason, setReason] = useState("");
  const [targetSubstudy, setTargetSubstudy] = useState("");
  const [consentConfirmed, setConsentConfirmed] = useState(false);
  const [correctError, setCorrectError] = useState<string | null>(null);
  // Guards the lookup + the three correction buttons. Without it a double-click
  // fires two PUTs, and every correction writes an audit event — so the second
  // one is a permanent, misleading "they changed it twice" in the trail.
  const [correctBusy, setCorrectBusy] = useState(false);

  const [deletions, setDeletions] = useState<AdminDeletionRequest[]>([]);
  const [deletionsLoaded, setDeletionsLoaded] = useState(false);
  const [deletionBusy, setDeletionBusy] = useState<number | null>(null);
  const [deletionError, setDeletionError] = useState<string | null>(null);
  const [deletionNotice, setDeletionNotice] = useState<string | null>(null);
  const [denyNote, setDenyNote] = useState("");

  // Record a deletion request received by study-team email (2026-08-24: the
  // in-app participant flow files withdrawals only).
  const [recordParticipantId, setRecordParticipantId] = useState("");
  const [recordNote, setRecordNote] = useState("");
  const [recordBusy, setRecordBusy] = useState(false);

  const reloadDeletions = useCallback(async () => {
    // A load failure must NOT render as an empty queue — this panel can be the
    // only signal that a permanent-deletion request is waiting for review.
    try {
      const rows = await api<AdminDeletionRequest[]>("GET", "/admin/deletion-requests");
      setDeletions(Array.isArray(rows) ? rows : []);
      setDeletionError(null);
    } catch (err) {
      setDeletions([]);
      setDeletionError(
        err instanceof ApiError ? err.message : "Could not load exit requests.",
      );
    } finally {
      setDeletionsLoaded(true);
    }
  }, []);

  useEffect(() => {
    void reloadDeletions();
  }, [reloadDeletions]);

  // Which pending deletion request awaits its confirm dialog (null = none).
  const [confirmExecuteId, setConfirmExecuteId] = useState<number | null>(null);
  // Which pending withdrawal request awaits its confirm dialog (null = none).
  const [confirmWithdrawId, setConfirmWithdrawId] = useState<number | null>(null);

  async function confirmWithdrawal(requestId: number): Promise<void> {
    setConfirmWithdrawId(null);
    setDeletionBusy(requestId);
    setDeletionError(null);
    setDeletionNotice(null);
    try {
      await api<AdminDeletionRequest>(
        "POST",
        `/admin/deletion-requests/${requestId}/withdraw`,
        { json: {} },
      );
      setDeletionNotice(`Request #${requestId} confirmed. Participant withdrawn, data retained.`);
      await reloadDeletions();
      onDeletionsResolved?.();
    } catch (err) {
      setDeletionError(
        err instanceof ApiError ? err.message : "Could not confirm the withdrawal.",
      );
    } finally {
      setDeletionBusy(null);
    }
  }

  async function executeDeletion(requestId: number): Promise<void> {
    setConfirmExecuteId(null);
    setDeletionBusy(requestId);
    setDeletionError(null);
    setDeletionNotice(null);
    try {
      const result = await api<DeletionExecuteResult>(
        "POST",
        `/admin/deletion-requests/${requestId}/execute`,
        { json: {} },
      );
      setDeletionNotice(
        `Request #${result.request_id} executed: ${result.photos_deleted} photo(s), ` +
          `${result.estimates_deleted} estimate(s), ${result.questionnaires_deleted} ` +
          `questionnaire(s) deleted.`,
      );
      await reloadDeletions();
      onDeletionsResolved?.();
    } catch (err) {
      setDeletionError(err instanceof ApiError ? err.message : "Could not execute the request.");
    } finally {
      setDeletionBusy(null);
    }
  }

  async function denyDeletion(requestId: number): Promise<void> {
    setDeletionBusy(requestId);
    setDeletionError(null);
    setDeletionNotice(null);
    try {
      await api<AdminDeletionRequest>("POST", `/admin/deletion-requests/${requestId}/deny`, {
        json: { note: denyNote.trim() },
      });
      setDeletionNotice(`Request #${requestId} denied.`);
      setDenyNote("");
      await reloadDeletions();
      onDeletionsResolved?.();
    } catch (err) {
      setDeletionError(err instanceof ApiError ? err.message : "Could not deny the request.");
    } finally {
      setDeletionBusy(null);
    }
  }

  async function recordDeletionRequest(): Promise<void> {
    setRecordBusy(true);
    setDeletionError(null);
    setDeletionNotice(null);
    try {
      const note = recordNote.trim();
      const created = await api<AdminDeletionRequest>(
        "POST",
        `/admin/participants/${recordParticipantId.trim()}/deletion-request`,
        { json: note ? { note } : {} },
      );
      setDeletionNotice(
        `Deletion request #${created.id} recorded for participant ${created.participant_id}.`,
      );
      setRecordParticipantId("");
      setRecordNote("");
      await reloadDeletions();
      // A new pending request changes the shell's tab-badge count too.
      onDeletionsResolved?.();
    } catch (err) {
      setDeletionError(
        err instanceof ApiError ? err.message : "Could not record the deletion request.",
      );
    } finally {
      setRecordBusy(false);
    }
  }

  async function search(): Promise<void> {
    setCorrectError(null);
    setFound(null);
    setCorrectBusy(true);
    try {
      setFound(
        await api<AdminParticipant[]>(
          "GET",
          `/admin/participants?email=${encodeURIComponent(searchEmail.trim())}`,
        ),
      );
    } catch (err) {
      setCorrectError(err instanceof ApiError ? err.message : "Lookup failed.");
    } finally {
      setCorrectBusy(false);
    }
  }

  async function correct(path: string, body: Record<string, unknown>): Promise<void> {
    setCorrectError(null);
    setCorrectBusy(true);
    try {
      await api<AdminParticipant>("PUT", path, { json: body });
    } catch (err) {
      setCorrectError(err instanceof ApiError ? err.message : "Update failed.");
      return;
    } finally {
      setCorrectBusy(false);
    }
    // Refresh the row. Left outside the try so a failed refresh reports itself
    // as a lookup failure — the correction above already succeeded, and saying
    // "Update failed" here would send the admin to re-apply a change that landed.
    await search();
  }

  return (
    <TabPanel id="participants" card={false}>
      <section className="app-card">
        <h2>Participant Corrections</h2>
        <p className="muted">
          Fix mis-signups: look a participant up by email, then attach them to a substudy,
          detach them from it, or withdraw them. Every change requires a reason and is
          audit-logged. Attaching requires confirming consent under that substudy&apos;s IRB is on
          file; a public consent does not transfer.
        </p>
        <div className="field-grid">
          <label className="field">
            <span>Participant email</span>
            <input
              type="email"
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
              placeholder="who@example.com"
            />
          </label>
        </div>
        <p>
          <button
            className="btn-ghost btn-sm"
            disabled={!searchEmail.trim() || correctBusy}
            onClick={() => void search()}
          >
            {correctBusy ? "Working…" : "Search"}
          </button>
        </p>
        {found !== null && found.length === 0 ? (
          <p className="muted">No participant found for that email.</p>
        ) : null}
        {found && found.length > 0 ? (
          <>
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Email</th>
                  <th>Substudy</th>
                  <th>Status</th>
                  <th>Account</th>
                </tr>
              </thead>
              <tbody>
                {found.map((p) => (
                  <tr key={p.id}>
                    <td>{p.id}</td>
                    <td>{p.email}</td>
                    <td>{p.substudy ?? "—"}</td>
                    <td>{p.enrollment_status.replace("_", " ")}</td>
                    <td>{p.has_account ? "yes" : "no"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="field-grid">
              <label className="field">
                <span>Reason (required, audit-logged)</span>
                <input
                  value={reason}
                  maxLength={500}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. registered publicly; belongs to the breast substudy"
                />
              </label>
              <label className="field">
                <span>Attach to substudy</span>
                <select value={targetSubstudy} onChange={(e) => setTargetSubstudy(e.target.value)}>
                  <option value="">Select…</option>
                  {substudies.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name} ({t.slug})
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <label className="field">
              <span>
                <input
                  type="checkbox"
                  checked={consentConfirmed}
                  onChange={(e) => setConsentConfirmed(e.target.checked)}
                />{" "}
                Consent under the target substudy&apos;s IRB is on file
              </span>
            </label>
            {found.map((p) => (
              <p key={p.id}>
                <strong>#{p.id}</strong>{" "}
                <button
                  className="btn-ghost btn-sm"
                  disabled={
                    correctBusy || reason.trim().length < 5 || !targetSubstudy || !consentConfirmed
                  }
                  onClick={() =>
                    void correct(`/admin/participants/${p.id}/substudy`, {
                      substudy_id: Number(targetSubstudy),
                      reason: reason.trim(),
                      consent_confirmed: consentConfirmed,
                    })
                  }
                >
                  Attach
                </button>{" "}
                <button
                  className="btn-ghost btn-sm"
                  disabled={correctBusy || reason.trim().length < 5 || p.substudy === null}
                  onClick={() =>
                    void correct(`/admin/participants/${p.id}/substudy`, {
                      substudy_id: null,
                      reason: reason.trim(),
                    })
                  }
                >
                  Detach
                </button>{" "}
                <button
                  className="btn-ghost btn-sm"
                  disabled={correctBusy || reason.trim().length < 5}
                  onClick={() =>
                    void correct(`/admin/participants/${p.id}/enrollment`, {
                      status: p.enrollment_status === "withdrawn" ? "active" : "withdrawn",
                      reason: reason.trim(),
                    })
                  }
                >
                  {p.enrollment_status === "withdrawn" ? "Reactivate" : "Withdraw"}
                </button>
              </p>
            ))}
          </>
        ) : null}
        {correctError ? <p className="form-error">{correctError}</p> : null}
      </section>

      <section className="app-card">
        <h2>Exit Requests</h2>
        <p className="muted">
          Requests to leave the study. Withdrawals are filed by the participant in the app:
          a withdrawal retains the data and stops new collection; confirm it here. Deletions
          are not filed in-app: the participant emails the study team, and you record the
          request below. A deletion is the purge: this panel is the only place it is carried
          out. Review the request, then execute (irreversible: photos, results, and
          questionnaires are removed and the account is closed; consent records are
          retained). Either type can be denied with a note.
        </p>
        <div className="field-grid">
          <label className="field">
            <span>Participant ID</span>
            <input
              type="number"
              value={recordParticipantId}
              onChange={(e) => setRecordParticipantId(e.target.value)}
            />
          </label>
          <label className="field">
            <span>Note (optional)</span>
            <input
              value={recordNote}
              maxLength={500}
              onChange={(e) => setRecordNote(e.target.value)}
              placeholder="e.g. emailed study team 2026-08-25"
            />
          </label>
        </div>
        <p>
          <button
            className="btn-ghost btn-sm"
            disabled={!recordParticipantId.trim() || recordBusy || deletionBusy !== null}
            onClick={() => void recordDeletionRequest()}
          >
            {recordBusy ? "Working…" : "Record deletion request…"}
          </button>
        </p>
        {!deletionsLoaded ? (
          <SkeletonRows rows={2} />
        ) : deletionError && deletions.length === 0 ? (
          <ErrorNotice>{deletionError}</ErrorNotice>
        ) : deletions.length === 0 ? (
          <p className="muted">No exit requests.</p>
        ) : (
          <>
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Type</th>
                    <th>Participant</th>
                    <th>Email</th>
                    <th>Reason</th>
                    <th>Note</th>
                    <th>Status</th>
                    <th>Requested</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {deletions.map((r) => (
                    <tr key={r.id}>
                      <td>{r.id}</td>
                      <td>
                        <span className={`status-pill ${r.request_type}`}>
                          {r.request_type === "deletion" ? "Deletion" : "Withdrawal"}
                        </span>
                      </td>
                      <td>{r.participant_id}</td>
                      <td>{r.participant_email}</td>
                      <td>{r.reason ? (REASON_LABELS[r.reason] ?? r.reason) : ""}</td>
                      <td>{r.note ?? ""}</td>
                      <td>{r.status}</td>
                      <td>{new Date(r.created_at).toLocaleDateString()}</td>
                      <td>
                        {r.status === "pending" ? (
                          <>
                            {r.request_type === "withdrawal" ? (
                              <button
                                className="btn-ghost btn-sm"
                                disabled={deletionBusy !== null}
                                onClick={() => setConfirmWithdrawId(r.id)}
                              >
                                {deletionBusy === r.id ? "Working…" : "Confirm withdrawal"}
                              </button>
                            ) : (
                              <button
                                className="btn-ghost btn-sm"
                                disabled={deletionBusy !== null}
                                onClick={() => setConfirmExecuteId(r.id)}
                              >
                                {deletionBusy === r.id ? "Working…" : "Execute"}
                              </button>
                            )}{" "}
                            <button
                              className="btn-ghost btn-sm"
                              disabled={deletionBusy !== null || denyNote.trim().length < 5}
                              onClick={() => void denyDeletion(r.id)}
                            >
                              Deny
                            </button>
                          </>
                        ) : (
                          (r.resolution_note ?? "")
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {deletions.some((r) => r.status === "pending") ? (
              <label className="field">
                <span>Denial note (required to deny, shared with the audit log)</span>
                <input
                  value={denyNote}
                  maxLength={500}
                  onChange={(e) => setDenyNote(e.target.value)}
                  placeholder="e.g. identity could not be verified"
                />
              </label>
            ) : null}
          </>
        )}
        {deletionError ? <p className="form-error">{deletionError}</p> : null}
        {deletionNotice ? <p className="form-ok">{deletionNotice}</p> : null}
        {confirmExecuteId !== null ? (
          <ConfirmDialog
            title="Execute Permanent Deletion"
            confirmLabel="Delete permanently"
            busyLabel="Deleting…"
            danger
            busy={deletionBusy !== null}
            onConfirm={() => void executeDeletion(confirmExecuteId)}
            onCancel={() => setConfirmExecuteId(null)}
          >
            <p className="muted">
              Permanently delete this participant&rsquo;s photos, results, and questionnaire
              answers and close their account? This cannot be undone.
            </p>
          </ConfirmDialog>
        ) : null}
        {confirmWithdrawId !== null ? (
          <ConfirmDialog
            title="Confirm Withdrawal"
            confirmLabel="Confirm withdrawal"
            busyLabel="Confirming…"
            busy={deletionBusy !== null}
            onConfirm={() => void confirmWithdrawal(confirmWithdrawId)}
            onCancel={() => setConfirmWithdrawId(null)}
          >
            <p className="muted">
              Confirm withdrawal for participant{" "}
              {deletions.find((r) => r.id === confirmWithdrawId)?.participant_id}? No data is
              deleted; the participant becomes withdrawn.
            </p>
          </ConfirmDialog>
        ) : null}
      </section>
    </TabPanel>
  );
}
